import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import ReduxUtils from "@shared/utils/ReduxUtils";
import {
  AccountGroup,
  DocumentType,
  DocumentTypesById,
  EntitiesState,
  EntitledAccount,
  EntitledFund,
  Fund,
  FundProfileDocumentTypesByFundId,
  Funds,
  Investor,
  Investors,
} from "./models";
import {
  fetchAccountGroups,
  fetchAllFunds,
  fetchDocumentsPageDocumentTypes,
  fetchDocumentTypes,
  fetchEntitledAccounts,
  fetchEntitledAccountsByEntity,
  fetchEntitledFunds,
  fetchFundProfileDocumentTypes,
  fetchInvestors,
} from "./thunks";

const toDocumentTypesById = (items: DocumentType[] | null | undefined): DocumentTypesById =>
  (items || []).reduce<DocumentTypesById>((result, documentType) => {
    result[documentType.documentTypeId] = documentType;

    return result;
  }, {});

const initialState: EntitiesState = {
  entitledAccountsByFundLoading: false,
  entitledAccounts: ReduxUtils.getAsyncSlice<Record<EntitledAccount["globalId"], EntitledAccount>>(),
  entitledFunds: ReduxUtils.getAsyncSlice<Record<EntitledFund["globalId"], EntitledFund>>(),
  entitledValidFunds: ReduxUtils.getAsyncSlice<Record<EntitledFund["globalId"], EntitledFund>>(),
  documentTypes: ReduxUtils.getAsyncSlice<DocumentTypesById>(),
  documentsPageDocumentTypes: ReduxUtils.getAsyncSlice<DocumentTypesById>(),
  fundProfileDocumentTypes: ReduxUtils.getAsyncSlice<FundProfileDocumentTypesByFundId>(),
  accountGroups: ReduxUtils.getAsyncSlice<AccountGroup[]>(),
  investors: ReduxUtils.getAsyncSlice<Investors>(),
  funds: ReduxUtils.getAsyncSlice<Funds>(),
};

const entitiesSlice = createSlice({
  name: "entities",
  initialState,
  reducers: {
    resetState: (state) => {
      state.entitledAccounts = initialState.entitledAccounts;
      state.entitledFunds = initialState.entitledFunds;
      state.entitledValidFunds = initialState.entitledValidFunds;
      state.documentTypes = initialState.documentTypes;
      state.documentsPageDocumentTypes = initialState.documentsPageDocumentTypes;
      state.fundProfileDocumentTypes = initialState.fundProfileDocumentTypes;
      state.investors = initialState.investors;
      state.accountGroups = initialState.accountGroups;
    },
    createAccountGroupLocally: (state, action: PayloadAction<AccountGroup>) => {
      state.accountGroups.data = state.accountGroups.data ?? [];
      state.accountGroups.data.push(action.payload);
    },
    updateAccountGroupLocally: (state, action: PayloadAction<AccountGroup>) => {
      const groups = state.accountGroups.data ?? [];

      state.accountGroups.data = groups.map((group) =>
        group.groupId === action.payload.groupId ? action.payload : group
      );
    },
    deleteAccountGroupLocally: (state, action: PayloadAction<AccountGroup["groupId"]>) => {
      const groups = state.accountGroups.data ?? [];

      state.accountGroups.data = groups.filter((group) => group.groupId !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder

      // fetchEntitledAccounts
      .addCase(fetchEntitledAccounts.pending, (state) => {
        state.entitledAccounts.loading = true;
        state.entitledAccounts.error = null;
      })
      .addCase(fetchEntitledAccounts.fulfilled, (state, action) => {
        state.entitledAccounts.loading = false;
        state.entitledAccounts.data = (action.payload || []).reduce((result, entitledAccount) => {
          result[entitledAccount.globalId] = entitledAccount;

          return result;
        }, {} as Record<string, EntitledAccount>);
      })
      .addCase(fetchEntitledAccounts.rejected, (state, action) => {
        state.entitledAccounts.loading = false;
        state.entitledAccounts.data = null;
        state.entitledAccounts.error = action.payload as string;
      })

      //fetchEntitledAccountsByEntity
      .addCase(fetchEntitledAccountsByEntity.pending, (state) => {
        state.entitledAccountsByFundLoading = true;
      })
      .addCase(fetchEntitledAccountsByEntity.fulfilled, (state, action) => {
        state.entitledAccountsByFundLoading = false;
        if (state.entitledFunds.data && state.entitledFunds.data[action.meta.arg]) {
          Object.assign(state.entitledFunds.data[action.meta.arg], {
            entitledAccountIds: (action.payload || []).map((ea) => ea.globalId),
          });
        }
      })
      .addCase(fetchEntitledAccountsByEntity.rejected, (state, action) => {
        state.entitledAccountsByFundLoading = false;
        if (state.entitledFunds.data && state.entitledFunds.data[action.meta.arg]) {
          Object.assign(state.entitledFunds.data[action.meta.arg], {
            entitledAccountIds: null,
          });
        }
      })

      // fetchInvestors
      .addCase(fetchInvestors.pending, (state) => {
        state.investors.loading = true;
        state.investors.error = null;
      })
      .addCase(fetchInvestors.fulfilled, (state, action) => {
        state.investors.loading = false;
        state.investors.data = (action.payload || []).reduce<Record<string, Investor>>((acc, investor) => {
          acc[investor.investorId] = investor;

          return acc;
        }, {});
      })
      .addCase(fetchInvestors.rejected, (state, action) => {
        state.investors.loading = false;
        state.investors.data = {};
        state.investors.error = action.payload as string;
      })

      // fetchEntitledFunds
      .addCase(fetchEntitledFunds.pending, (state) => {
        state.entitledFunds.loading = true;
        state.entitledFunds.error = null;
      })
      .addCase(fetchEntitledFunds.fulfilled, (state, action) => {
        state.entitledFunds.loading = false;
        state.entitledFunds.data = (action.payload || []).reduce((result, entitledFund) => {
          result[entitledFund.globalId] = {
            ...entitledFund,
            entitledAccountIds: null,
          };

          return result;
        }, {} as Record<string, EntitledFund>);

        state.entitledValidFunds.data = (action.payload || []).reduce((result, entitledFund) => {
          const currdate = entitledFund.currentDate ? entitledFund.currentDate : new Date();

          if (entitledFund.endDate === null || entitledFund.endDate >= currdate) {
            result[entitledFund.globalId] = {
              ...entitledFund,
              entitledAccountIds: null,
            };
          }

          return result;
        }, {} as Record<string, EntitledFund>);
      })
      .addCase(fetchEntitledFunds.rejected, (state, action) => {
        state.entitledFunds.loading = false;
        state.entitledFunds.data = null;
        state.entitledFunds.error = action.payload as string;
      })

      // fetchDocumentTypes (full entitled catalog; Administration Reports)
      .addCase(fetchDocumentTypes.pending, (state) => {
        state.documentTypes.loading = true;
        state.documentTypes.error = null;
      })
      .addCase(fetchDocumentTypes.fulfilled, (state, action) => {
        state.documentTypes.loading = false;
        state.documentTypes.data = toDocumentTypesById(action.payload);
      })
      .addCase(fetchDocumentTypes.rejected, (state, action) => {
        state.documentTypes.loading = false;
        state.documentTypes.data = null;
        state.documentTypes.error = action.payload as string;
      })

      // fetchDocumentsPageDocumentTypes (Documents tab)
      .addCase(fetchDocumentsPageDocumentTypes.pending, (state) => {
        state.documentsPageDocumentTypes.loading = true;
        state.documentsPageDocumentTypes.error = null;
      })
      .addCase(fetchDocumentsPageDocumentTypes.fulfilled, (state, action) => {
        state.documentsPageDocumentTypes.loading = false;
        state.documentsPageDocumentTypes.data = toDocumentTypesById(action.payload);
      })
      .addCase(fetchDocumentsPageDocumentTypes.rejected, (state, action) => {
        state.documentsPageDocumentTypes.loading = false;
        state.documentsPageDocumentTypes.data = null;
        state.documentsPageDocumentTypes.error = action.payload as string;
      })

      // fetchFundProfileDocumentTypes (per-fund Fund profile)
      .addCase(fetchFundProfileDocumentTypes.pending, (state) => {
        state.fundProfileDocumentTypes.loading = true;
        state.fundProfileDocumentTypes.error = null;
      })
      .addCase(fetchFundProfileDocumentTypes.fulfilled, (state, action) => {
        state.fundProfileDocumentTypes.loading = false;

        const { fundGlobalId, items } = action.payload;
        const existing = state.fundProfileDocumentTypes.data ?? {};

        state.fundProfileDocumentTypes.data = {
          ...existing,
          [fundGlobalId]: toDocumentTypesById(items),
        };
      })
      .addCase(fetchFundProfileDocumentTypes.rejected, (state, action) => {
        state.fundProfileDocumentTypes.loading = false;
        state.fundProfileDocumentTypes.error = action.payload as string;
      })

      // fetchAccountGroups
      .addCase(fetchAccountGroups.pending, (state) => {
        state.accountGroups.loading = true;
        state.accountGroups.error = null;
      })
      .addCase(fetchAccountGroups.fulfilled, (state, action) => {
        state.accountGroups.loading = false;
        const payload = action.payload ?? [];

        state.accountGroups.data = payload.map((g) => ({
          entitledUserId: g.entitledUserId,
          groupName: g.groupName,
          groupId: g.groupId,
          investorAccountIds: (g.investorAccounts ?? []).map((ia) => Number(ia.id)),
        }));
      })
      .addCase(fetchAccountGroups.rejected, (state, action) => {
        state.accountGroups.loading = false;
        state.accountGroups.data = [];
        state.accountGroups.error = action.payload as string;
      })

      // fetchAllFunds
      .addCase(fetchAllFunds.pending, (state) => {
        state.funds.loading = true;
        state.funds.error = null;
      })
      .addCase(fetchAllFunds.fulfilled, (state, action) => {
        state.funds.loading = false;
        state.funds.error = null;
        state.funds.data = (action.payload || []).reduce((acc, fund: Fund) => {
          acc[fund.id] = fund;
          // fund.type = "";

          return acc;
        }, {} as Record<string, Fund>);
      })
      .addCase(fetchAllFunds.rejected, (state, action) => {
        state.funds.loading = false;
        state.funds.error = action.error.message!;

        state.funds.data = null;
      });
  },
});

export const { resetState, createAccountGroupLocally, updateAccountGroupLocally, deleteAccountGroupLocally } =
  entitiesSlice.actions;
export default entitiesSlice.reducer;

/**
 * Business Exception: Hardcoded Internal Funds Allowlist
 * These specific internal funds bypass the fundraisingStatus/entityType check
 * and are always eligible for Wire Instructions.
 */
const WIRE_INSTRUCTIONS_INTERNAL_FUNDS_ALLOWLIST = [
  "F000122524", // Carlyle Carried Interest
  "F000122525", // Carlyle Equity Pool
  "F000122526", // Carlyle Internal Coinvestment
  "F000122523", // Carlyle Holdings Units
] as const;

/**
 * Checks if a fund is in a self-service-eligible stage (fundraisingStatus = "Closed" or
 * "Fundraising", OR entityType = "Open Ended"). Mirrors the contact-service self-service
 * fund-stage filter. All checks are case-insensitive.
 */
const isFundStatusClosed = (fund: EntitledFund): boolean => {
  const fundraisingStatus = (fund.fundraisingStatus || "").toLowerCase();
  const entityType = (fund.entityType || "").toLowerCase();

  return fundraisingStatus === "closed" || fundraisingStatus === "fundraising" || entityType === "open ended";
};

/**
 * Checks if an Internal fund is in the business exception allowlist
 */
const isInternalFundAllowlisted = (fund: EntitledFund): boolean => {
  return WIRE_INSTRUCTIONS_INTERNAL_FUNDS_ALLOWLIST.includes(fund.globalId as any);
};

// Determine whether the user has access to internal and/or external funds
// NO AlpInvest or Real Estate exclusions - users should see tabs for ALL their entitled funds
export const selectInternalExternalPresence = (state: { entities: EntitiesState }) => {
  const funds = state.entities.entitledFunds.data;

  if (!funds) return { hasInternal: false, hasExternal: false };

  const values = Object.values(funds);

  // Check for Internal funds: ONLY accept the 4 specific allowlisted funds
  // Business requirement: Only these funds should be considered as internal funds:
  // - Carlyle Carried Interest (F000122524)
  // - Carlyle Equity Pool (F000122525)
  // - Carlyle Internal Coinvestment (F000122526)
  // - Carlyle Holdings Units (F000122523)
  const hasInternal = values.some((f) => {
    // First check if fund is in allowlist
    if (!isInternalFundAllowlisted(f)) {
      return false;
    }

    // Defensive checks: Even for allowlisted funds, verify basic criteria
    // to ensure data integrity
    if (f.processArea !== "Internal" || !f.includeInDmltFeed) {
      return false;
    }

    return true;
  });

  // Check for External funds: must have closed status (no allowlist exception)
  const hasExternal = values.some((f) => {
    const processArea = String(f.processArea || "")
      .trim()
      .toLowerCase();

    if (!processArea.startsWith("external") || !f.includeInDmltFeed) {
      return false;
    }

    return isFundStatusClosed(f);
  });

  return { hasInternal, hasExternal };
};

/**
 * Manage Contacts–specific fund qualification.
 * Uses generic flag logic confirmed by BA (LPC-10530 Jira comment):
 *   processArea + includeInDmltFeed + closed status
 * No internal fund ID allowlist — separate from Wire Instructions logic above.
 */
export const selectMCInternalExternalPresence = createSelector(
  (state: { entities: EntitiesState }) => state.entities.entitledFunds.data,
  (funds): { hasInternal: boolean; hasExternal: boolean } => {
    if (!funds) return { hasInternal: false, hasExternal: false };

    const values = Object.values(funds);

    const hasInternal = values.some((f) => {
      const processArea = String(f.processArea || "")
        .trim()
        .toLowerCase();

      if (processArea !== "internal" || !f.includeInDmltFeed) {
        return false;
      }

      return isFundStatusClosed(f);
    });

    const hasExternal = values.some((f) => {
      const processArea = String(f.processArea || "")
        .trim()
        .toLowerCase();

      if (!processArea.startsWith("external") || !f.includeInDmltFeed) {
        return false;
      }

      return isFundStatusClosed(f);
    });

    return { hasInternal, hasExternal };
  }
);

/**
 * Returns the set of entitled-fund globalIds that qualify as internal (same rule as
 * `selectMCInternalExternalPresence`), so a hybrid table can classify each row individually.
 *
 * The feed collapses a sub-fund onto its parent's globalId, so a row for an internal sub-fund
 * carries the parent's globalId. We therefore also index the parent entity's globalId for every
 * qualifying internal fund so the collapsed row still resolves as internal.
 */
export const selectInternalFundGlobalIds = createSelector(
  (state: { entities: EntitiesState }) => state.entities.entitledFunds.data,
  (funds): Set<string> => {
    const internalIds = new Set<string>();

    if (!funds) return internalIds;

    Object.values(funds).forEach((f) => {
      const processArea = String(f.processArea || "")
        .trim()
        .toLowerCase();

      if (processArea === "internal" && f.includeInDmltFeed && isFundStatusClosed(f)) {
        if (f.globalId) internalIds.add(f.globalId);
        if (f.parentEntitledEntity?.globalId) internalIds.add(f.parentEntitledEntity.globalId);
      }
    });

    return internalIds;
  }
);
